// Repo knowledge-base reads (arc 101 slice 07 + the search route) — the UI half
// of a capability that had a REST door (`/v1/kb/:repoId/*`) and an MCP one
// (`kb_search` / `kb_page_get`) and no human one. The KB is what every later
// agent reasons FROM, so a maintainer who cannot read it cannot tell a wrong
// answer from a wrong page.
//
// `read:kb` is viewer-grade (`packages/mcp/src/tools.ts`), so every role reads
// this and there is no client role gate — a refused call renders the API's own
// 403, the call `/artifacts` and `/account/templates` make.
//
// THE ADDRESS IS THE REPO's UUID, not `owner/repo` — the one repo surface that
// is (the MCP verbs take the full name; these routes take the id). So the tab
// waits on the repo row for its id, exactly as the Secrets tab does, rather than
// deriving it from the route param.
//
// THREE READS, THREE SHAPES, and they are deliberately not interchangeable:
//
//   - `GET …/pages` is a real `Page` envelope with a REAL cursor (the route
//     pushes `offset`/`sort` into SQL, since a repo's wiki can outgrow one
//     unbounded read). So this is `usePagedQuery` + a Load-more that means it.
//   - `GET …/search` is NOT an envelope: the ranked head mints no cursor, and
//     the route says why (a permanent `next_cursor: null` would claim "that was
//     all" about a truncation). `?limit=` is its own 1..`KB_SEARCH_LIMIT_MAX` window, refused past
//     it rather than clamped, so this asks for the ceiling and never more.
//   - `GET …/pages/:slug` is the point read behind the page panel.
//
// THE PANEL POINT-READS EVEN THOUGH THE INDEX CARRIES `text`. Two reasons, and
// the first is the load-bearing one: a search HIT carries an excerpt, never the
// body, so a panel fed from the list could show nothing for the surface people
// reach it from. The second is freshness — this wiki is written by agents while
// a human is reading it, so the panel shows what is stored now rather than
// whatever the index page was holding (`RepoPendingTab`'s rule: the list answers
// the table, the point read answers the panel).

import type { KbPageWire } from '@developerz/domain';
import { KB_SEARCH_LIMIT_MAX, KbPageWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { tPlural } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * One wiki page — `KbPageWireSchema` from `@developerz/domain`, THE PUBLISHED
 * RESOURCE (`KbPageResource`), not a copy of it.
 *
 * `text` is the full markdown body and is rendered only through
 * `renderMarkdown` — it is agent-authored. `links` is the cross-link set a full
 * page write replaces.
 */
export type KbPageRow = Readonly<KbPageWire>;

/** One page of the wiki index — the canonical `{ data, next_cursor }` shape. */
export type KbPagesPage = Page<KbPageRow>;

/**
 * The index body, PARSED (plan 101 slice 15 step 3). The point read below parses
 * through the bare `KbPageWireSchema`, so the two reads this file's header calls
 * deliberately-not-interchangeable are checked against the SAME row shape — the
 * one property they do share.
 *
 * The search route is not parsed here: its hit is `KbSearchHitView`, an
 * API-local ranking projection with no resource in `packages/domain`, so there
 * is nothing published to parse against and a schema written here would be the
 * second declaration this seam exists to delete.
 */
const kbPagesSchema = pageSchema(KbPageWireSchema);

/**
 * One search hit — the API's `KbSearchHitView`. `excerpt` is a PREVIEW the
 * server derives (first non-empty line, capped); the body is load-on-demand
 * through the page read, which is the whole point of the two shapes.
 */
export type KbSearchHit = {
  readonly slug: string;
  readonly score: number;
  readonly links: readonly string[];
  readonly excerpt: string;
};

export const KB_QUERY_KEY = 'repo-kb';

/** Index page size. Well inside the route's ceiling — a wiki index is a browse surface. */
export const KB_PAGE_SIZE = 20;

/**
 * The search route's own `limit` ceiling ({@link KB_SEARCH_LIMIT_MAX}, the domain
 * constant the route's schema cites). Asked for on every search so
 * one round trip ranks as widely as the server will, and refused — not clamped —
 * past it, which is why nothing here may raise it.
 */
export const KB_SEARCH_LIMIT = KB_SEARCH_LIMIT_MAX;

/**
 * "retrieved N times" — the wiki's own usefulness signal, and the one number
 * both the index row and the page panel print. One function so the two cannot
 * disagree about the plural form of the same count.
 */
export function kbRetrievedLabel(count: number): string {
  return tPlural('repo_kb.retrieved', count, { count: String(count) });
}

function kbBase(repoId: string): string {
  return `/v1/kb/${encodeURIComponent(repoId)}`;
}

/**
 * Address one page. A slug may NAMESPACE with `/` ("arch/auth-flow") and the
 * route captures the remainder whole (`:slug{.+}`), so the separator stays a
 * real path separator and only the segments are encoded. `%2F` happens to
 * decode the same way in Hono, but it is the form proxies normalise or refuse,
 * and the literal one is what `kb.test.ts` proves the route answers.
 *
 * WHICH IS WHY THE DOT SEGMENTS ARE REFUSED HERE. Keeping `/` literal means the
 * slug is steering a real URL path, and `encodeURIComponent` leaves `.` alone —
 * so a slug carrying `..` would resolve the request OUT of `/pages/` and at some
 * neighbouring route entirely, silently, with a 200. Today's server alphabet
 * (`KB_SLUG_PATTERN`) admits no `.` at all so this cannot fire; that is exactly
 * why it is a throw and not a fallback — if it ever does, the slug came from
 * somewhere that stopped agreeing with the route, and a loud failure at the seam
 * beats a quiet request to an address nobody chose.
 */
export function kbPagePath(repoId: string, slug: string): string {
  const segments = slug.split('/');
  if (segments.some((s) => s === '' || s === '.' || s === '..')) {
    throw new Error(`kb: slug is not addressable: ${JSON.stringify(slug)}`);
  }
  return `${kbBase(repoId)}/pages/${segments.map(encodeURIComponent).join('/')}`;
}

/** One page of the wiki index, `slug asc` (the route's default sort). */
export function useKbPages(
  repoId: () => string,
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<KbPagesPage>(() => ({
    queryKey: [KB_QUERY_KEY, 'pages', repoId(), cursor(), token, activeAccountId()],
    enabled: token !== null && repoId() !== '',
    queryFn: () => {
      const params = new URLSearchParams({ per_page: String(KB_PAGE_SIZE) });
      const at = cursor();
      if (at) params.set('cursor', at);
      return apiFetch(`${kbBase(repoId())}/pages?${params.toString()}`, token, undefined, {
        schema: kbPagesSchema,
      });
    },
  }));
}

/**
 * Trigram-ranked hits for one query. Idle on an empty query — a blank search box
 * is not a search, and `?q=` is a required 1..2 000 field the route 400s without.
 */
export function useKbSearch(
  repoId: () => string,
  query: () => string,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<{ hits: readonly KbSearchHit[] }>(() => ({
    queryKey: [KB_QUERY_KEY, 'search', repoId(), query(), token, activeAccountId()],
    enabled: token !== null && repoId() !== '' && query() !== '',
    queryFn: () => {
      const params = new URLSearchParams({ q: query(), limit: String(KB_SEARCH_LIMIT) });
      return apiFetch<{ hits: readonly KbSearchHit[] }>(
        `${kbBase(repoId())}/search?${params.toString()}`,
        token,
      );
    },
  }));
}

/** One page verbatim. Idle until a slug is selected; a 404 is an ANSWER, not a fault. */
export function useKbPage(
  repoId: () => string,
  slug: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<KbPageRow>(() => ({
    queryKey: [KB_QUERY_KEY, 'page', repoId(), slug(), token, activeAccountId()],
    enabled: token !== null && repoId() !== '' && slug() !== null,
    queryFn: () =>
      apiFetch(kbPagePath(repoId(), slug() ?? ''), token, undefined, { schema: KbPageWireSchema }),
  }));
}
